import * as React from 'react';
import { Nullable, OrgComponentProps } from '../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import InputNumber from 'antd/lib/input-number';
import Text from 'antd/lib/typography/Text';
import Checkbox from 'antd/lib/checkbox';
import { RelativeTiming, LifecycleEventType, ReleaseScheduleCadenceStyle, RelativeTimeTrigger } from '../types';
import Select from 'antd/lib/select';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectScheduleSettings } from '../selectors/selectors';
import { useWatch } from 'antd/lib/form/Form';
import { Form } from 'antd/lib';
import { runwayColors } from './styles/globalStyles';
import {
    ROLLOUT_THRESHOLD_LIFECYCLE_EVENT_TYPES,
    useRelativeTimeTriggerOptions,
} from '@/utils/hooks/useRelativeTimeTriggerOptions';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import { DEFAULT_ROLLOUT_SEQUENCE } from '../constants';

const MULTI_SELECTOR_KEY = 'lifecycleEventType+waitForAllDestinations';
const REQUIRED_FIELD_RULES = [{ required: true, message: 'Required' }];
// Triggers that were never set are saved with an empty timing and event; show those as unset so the placeholder appears
const getSelectValueProps = (value: string) => ({ value: value || undefined });

export const DEFAULT_RELATIVE_TIME_TRIGGER: RelativeTimeTrigger = {
    enabled: false,
    relativeDays: 0,
    relativeHours: 0,
    relativeMinutes: 0,
    timing: RelativeTiming.onTime,
    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
    waitForAllDestinations: false,
    allowTriggeringBasedOnTargetDate: false,
    rolloutThreshold: 0,
};

interface RelativeTimeTriggerSettingsProps extends OrgComponentProps {
    appId?: string;
    stepConfigId: Nullable<string>;
    initialValues: Partial<RelativeTimeTrigger>;
    parentFieldName?: string | (string | number)[];
    prefixPath?: (string | number)[];
    isOrgLevelItem?: boolean;
    showAllowTriggeringBasedOnTargetDate?: boolean;
    label?: string;
    lifecycleEventTypes?: LifecycleEventType[];
    isBeforeTimingHidden?: boolean;
}

export const RelativeTimeTriggerSettings: React.FC<RelativeTimeTriggerSettingsProps> = ({
    appId,
    stepConfigId,
    initialValues,
    parentFieldName = 'relativeTimeTrigger',
    prefixPath = [],
    isOrgLevelItem,
    showAllowTriggeringBasedOnTargetDate = false,
    label,
    lifecycleEventTypes,
    isBeforeTimingHidden = false,
}: RelativeTimeTriggerSettingsProps) => {
    const form = Form.useFormInstance();
    const fieldPath = React.useMemo(
        () => (Array.isArray(parentFieldName) ? parentFieldName : [parentFieldName]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(parentFieldName)],
    );

    const absoluteFieldPath = React.useMemo(
        () => [...prefixPath, ...fieldPath],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(prefixPath), fieldPath],
    );

    const selectedLifecycleEventType = useWatch<LifecycleEventType>([...absoluteFieldPath, 'lifecycleEventType']);
    const selectedTiming = useWatch<RelativeTiming>([...absoluteFieldPath, 'timing']);

    // Watched values are empty until the form is filled, so render from the initial values to avoid a flash on open
    const currentLifecycleEventType =
        selectedLifecycleEventType ||
        initialValues.lifecycleEventType ||
        DEFAULT_RELATIVE_TIME_TRIGGER.lifecycleEventType;

    const currentTiming = selectedTiming ?? initialValues.timing ?? DEFAULT_RELATIVE_TIME_TRIGGER.timing;
    const multiSelectorValue = useWatch<string>([...absoluteFieldPath, MULTI_SELECTOR_KEY]);
    const scheduleSettings = useSelector((state: RootState) => (appId ? selectScheduleSettings(state, appId) : null));
    const hasActiveSchedule =
        scheduleSettings ? scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never : false;

    // Rollout events are estimated from the rollout itself, so only these need a schedule target day to fire early
    const getIsMissingScheduleTargetForBefore = React.useCallback(
        (lifecycleEventType: LifecycleEventType) => {
            if (isOrgLevelItem) return false;

            const targetDateSet = hasActiveSchedule ? scheduleSettings?.cadenceTargetDateSets?.[0] : undefined;
            switch (lifecycleEventType) {
                case LifecycleEventType.LifecycleEventTypeKickoff:
                    return !targetDateSet?.kickoffDayOfWeek;
                case LifecycleEventType.LifecycleEventTypeSubmit:
                    return !targetDateSet?.submitDayOfWeek;
                case LifecycleEventType.LifecycleEventTypeRelease:
                    return !targetDateSet?.releaseDayOfWeek;
                default:
                    return false;
            }
        },
        [isOrgLevelItem, hasActiveSchedule, scheduleSettings],
    );

    const {
        lifecycleEventOptions,
        relativeTimingOptions,
        parseSelectedLifecycleEventOption,
        convertToSelectedLifecycleEventValue,
    } = useRelativeTimeTriggerOptions(appId, stepConfigId);

    const visibleLifecycleEventOptions =
        lifecycleEventTypes ?
            lifecycleEventOptions.filter((option) =>
                lifecycleEventTypes.includes(parseSelectedLifecycleEventOption(option.value).lifecycleEventType),
            )
        :   lifecycleEventOptions;

    const visibleRelativeTimingOptions =
        isBeforeTimingHidden ?
            relativeTimingOptions.filter((option) => option.value !== RelativeTiming.before)
        :   relativeTimingOptions;

    const stableInitialValues = React.useMemo(
        () => initialValues,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            initialValues.allowTriggeringBasedOnTargetDate,
            initialValues.enabled,
            initialValues.lifecycleEventType,
            initialValues.relativeDays,
            initialValues.relativeHours,
            initialValues.relativeMinutes,
            initialValues.timing,
            initialValues.waitForAllDestinations,
            initialValues.rolloutThreshold,
        ],
    );

    React.useEffect(() => {
        const trigger: RelativeTimeTrigger & { [MULTI_SELECTOR_KEY]?: string } = {
            ...DEFAULT_RELATIVE_TIME_TRIGGER,
            ...stableInitialValues,
        };

        const initialSelectedEventOption = convertToSelectedLifecycleEventValue(
            trigger.lifecycleEventType,
            trigger.waitForAllDestinations,
        );

        trigger[MULTI_SELECTOR_KEY] = initialSelectedEventOption;
        const currentEnabled = form.getFieldValue([...absoluteFieldPath, 'enabled']);
        if (currentEnabled !== undefined) {
            trigger.enabled = currentEnabled;
        }
        form.setFieldValue(absoluteFieldPath, trigger);
    }, [form, absoluteFieldPath, stableInitialValues, convertToSelectedLifecycleEventValue]);

    React.useEffect(() => {
        if (selectedTiming === RelativeTiming.onTime) {
            form.setFieldValue([...absoluteFieldPath, 'relativeDays'], 0);
            form.setFieldValue([...absoluteFieldPath, 'relativeHours'], 0);
            form.setFieldValue([...absoluteFieldPath, 'relativeMinutes'], 0);
        }
    }, [form, absoluteFieldPath, selectedTiming]);

    React.useEffect(() => {
        if (multiSelectorValue) {
            const parsedValue = parseSelectedLifecycleEventOption(multiSelectorValue);
            const lifecycleEventTypePath = [...absoluteFieldPath, 'lifecycleEventType'];
            const previousLifecycleEventType = form.getFieldValue(lifecycleEventTypePath);
            form.setFieldValue(lifecycleEventTypePath, parsedValue.lifecycleEventType);
            form.setFieldValue([...absoluteFieldPath, 'waitForAllDestinations'], parsedValue.waitForAllDestinations);
            const rolloutThresholdPath = [...absoluteFieldPath, 'rolloutThreshold'];
            if (!ROLLOUT_THRESHOLD_LIFECYCLE_EVENT_TYPES.includes(parsedValue.lifecycleEventType)) {
                form.setFieldValue(rolloutThresholdPath, 0);
            } else if (
                previousLifecycleEventType !== parsedValue.lifecycleEventType ||
                !form.getFieldValue(rolloutThresholdPath)
            ) {
                form.setFieldValue(rolloutThresholdPath, 1);
            }
        }
    }, [form, absoluteFieldPath, multiSelectorValue, parseSelectedLifecycleEventOption]);

    const handleTimingChange = (timing: RelativeTiming) => {
        if (timing !== RelativeTiming.before || !getIsMissingScheduleTargetForBefore(currentLifecycleEventType)) {
            return;
        }
        const firstAllowedEventOption = visibleLifecycleEventOptions.find(
            (option) =>
                !getIsMissingScheduleTargetForBefore(
                    parseSelectedLifecycleEventOption(option.value).lifecycleEventType,
                ),
        );
        form.setFieldValue([...absoluteFieldPath, MULTI_SELECTOR_KEY], firstAllowedEventOption?.value);
    };

    const lifecycleEventSelectOptions = visibleLifecycleEventOptions.map((option) => ({
        ...option,
        disabled:
            currentTiming === RelativeTiming.before &&
            getIsMissingScheduleTargetForBefore(parseSelectedLifecycleEventOption(option.value).lifecycleEventType),
    }));

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: 12,
                    alignItems: 'center',
                    marginBottom: 0,
                    width: '100%',
                }}
            >
                {label && <Text style={{ whiteSpace: 'nowrap' }}>{label}</Text>}
                <FormItem
                    name={[...fieldPath, 'relativeDays']}
                    rules={REQUIRED_FIELD_RULES}
                    style={{ marginBottom: 0, marginTop: 0 }}
                >
                    <InputNumber
                        min={0}
                        disabled={currentTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">d</Text>}
                    />
                </FormItem>
                <FormItem
                    name={[...fieldPath, 'relativeHours']}
                    rules={REQUIRED_FIELD_RULES}
                    style={{ marginBottom: 0, marginTop: 0 }}
                >
                    <InputNumber
                        min={0}
                        max={23}
                        disabled={currentTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">h</Text>}
                    />
                </FormItem>
                <FormItem
                    name={[...fieldPath, 'relativeMinutes']}
                    rules={REQUIRED_FIELD_RULES}
                    style={{ marginBottom: 0, marginTop: 0 }}
                >
                    <InputNumber
                        min={0}
                        max={59}
                        disabled={currentTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">m</Text>}
                    />
                </FormItem>
                <FormItem
                    name={[...fieldPath, 'timing']}
                    rules={REQUIRED_FIELD_RULES}
                    getValueProps={getSelectValueProps}
                    style={{
                        marginBottom: 0,
                        minWidth: label ? 90 : 150,
                        flex: 1,
                    }}
                >
                    <Select
                        placeholder="at the time of"
                        options={visibleRelativeTimingOptions}
                        onChange={handleTimingChange}
                    />
                </FormItem>
                <FormItem
                    name={[...fieldPath, MULTI_SELECTOR_KEY]}
                    rules={REQUIRED_FIELD_RULES}
                    getValueProps={getSelectValueProps}
                    style={{ minWidth: label ? 90 : 120, flex: 2, marginBottom: 0, marginTop: 0 }}
                >
                    <Select placeholder="Kickoff" options={lifecycleEventSelectOptions} />
                </FormItem>
                {currentLifecycleEventType === LifecycleEventType.LifecycleEventTypeRolloutPercentage && (
                    <FormItem
                        name={[...fieldPath, 'rolloutThreshold']}
                        rules={REQUIRED_FIELD_RULES}
                        style={{ marginBottom: 0, marginTop: 0 }}
                    >
                        <InputNumber
                            min={1}
                            max={100}
                            precision={0}
                            style={rolloutThresholdInputStyle}
                            suffix={<Text type="secondary">%</Text>}
                        />
                    </FormItem>
                )}
                {currentLifecycleEventType === LifecycleEventType.LifecycleEventTypeRolloutDay && (
                    <FormItem
                        name={[...fieldPath, 'rolloutThreshold']}
                        rules={REQUIRED_FIELD_RULES}
                        style={{ marginBottom: 0, marginTop: 0 }}
                    >
                        <InputNumber
                            min={1}
                            max={DEFAULT_ROLLOUT_SEQUENCE.length}
                            precision={0}
                            style={rolloutThresholdInputStyle}
                            prefix={<Text type="secondary">Day</Text>}
                        />
                    </FormItem>
                )}
                <FormItem name={[...fieldPath, 'lifecycleEventType']} noStyle style={{ width: 0, height: 0 }} />
                <FormItem name={[...fieldPath, 'waitForAllDestinations']} noStyle style={{ width: 0, height: 0 }} />
            </div>

            {lifecycleEventSelectOptions.some((option) => option.disabled) && (
                <div style={{ marginTop: 8, fontSize: 12, color: runwayColors.statusGrey }}>
                    Setting target times before kickoff, submit or release requires a target day for that event in
                    Schedule settings.
                </div>
            )}
            {showAllowTriggeringBasedOnTargetDate && currentTiming !== RelativeTiming.before && (
                <FormItem
                    name={[...fieldPath, 'allowTriggeringBasedOnTargetDate']}
                    valuePropName="checked"
                    style={{ marginBottom: 0, marginTop: 12 }}
                >
                    <Checkbox>
                        Allow triggering based on target date
                        <TooltipInfoIcon
                            tooltipContent="If configured to trigger on or after an event, allow triggering based on the target date even if the event hasn't occurred yet"
                            style={{ marginLeft: 8 }}
                        />
                    </Checkbox>
                </FormItem>
            )}
            {isOrgLevelItem && (
                <div className="ant-form-item-explain" style={{ color: runwayColors.statusGrey }}>
                    <ul
                        style={{
                            paddingLeft: 16,
                            fontSize: '12px',
                            paddingBottom: 0,
                            marginBottom: 0,
                            marginTop: 10,
                            color: runwayColors.statusGrey,
                        }}
                    >
                        <li>
                            Setting target times relative to a lifecycle event will only work if the lifecycle event is
                            configured to exist for each applicable step.
                        </li>
                        {currentTiming === RelativeTiming.before && (
                            <li>
                                Setting target times before lifecycle events requires a schedule cadence to be set in
                                every app's Schedule settings.
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

const timeInputStyle: React.CSSProperties = {
    width: 70,
    textAlign: 'center',
    borderRadius: 8,
};

const rolloutThresholdInputStyle: React.CSSProperties = {
    ...timeInputStyle,
    width: 100,
};
